import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "layoutType", "rows", "columns", "gridDisplay", "gridConfig", "configField", "seatCount", "capacityWarning",
    "rowConfig", "rowLayoutRows", "rowDetails", "rowSeatCount", "rowCapacityWarning",
    "freeFormConfig", "freeFormSeats", "freeFormDetails", "freeFormSeatCount", "freeFormCapacityWarning",
    "bulkActions"
  ]
  static values = { roomCapacity: Number }

  connect() {
    console.log("[SeatLayoutBuilder] Controller connected")
    console.log("[SeatLayoutBuilder] Targets:", {
      layoutType: this.hasLayoutTypeTarget,
      rows: this.hasRowsTarget,
      columns: this.hasColumnsTarget,
      gridDisplay: this.hasGridDisplayTarget,
      gridConfig: this.hasGridConfigTarget,
      configField: this.hasConfigFieldTarget
    })

    this.blockedSeats = new Set()
    this.customLabels = {}

    // Load existing configuration if editing
    if (this.configFieldTarget.value) {
      try {
        const config = JSON.parse(this.configFieldTarget.value)
        this.blockedSeats = new Set(config.blocked || [])
        this.customLabels = config.labels || {}
      } catch (e) {
        console.error("Error parsing seat configuration:", e)
      }
    }

    // Use setTimeout to ensure form values are loaded before generating grid
    setTimeout(() => {
      console.log("[SeatLayoutBuilder] Calling handleLayoutTypeChange")
      this.handleLayoutTypeChange()
    }, 100)
  }

  handleLayoutTypeChange() {
    const layoutType = this.layoutTypeTarget.value
    console.log("[SeatLayoutBuilder] Layout type:", layoutType)

    // Hide all configuration sections and disable their inputs
    this.gridConfigTarget.style.display = 'none'
    this.disableInputs(this.gridConfigTarget)

    if (this.hasRowConfigTarget) {
      this.rowConfigTarget.style.display = 'none'
      this.disableInputs(this.rowConfigTarget)
    }

    if (this.hasFreeFormConfigTarget) {
      this.freeFormConfigTarget.style.display = 'none'
      this.disableInputs(this.freeFormConfigTarget)
    }

    // Show the appropriate configuration section and enable its inputs
    if (layoutType === 'grid') {
      console.log("[SeatLayoutBuilder] Showing grid config")
      this.gridConfigTarget.style.display = 'block'
      this.enableInputs(this.gridConfigTarget)
      this.generateGrid()
    } else if (layoutType === 'rows') {
      console.log("[SeatLayoutBuilder] Showing row config")
      if (this.hasRowConfigTarget) {
        this.rowConfigTarget.style.display = 'block'
        this.enableInputs(this.rowConfigTarget)
        this.generateRowLayout()
      }
    } else if (layoutType === 'free_form') {
      console.log("[SeatLayoutBuilder] Showing free-form config")
      if (this.hasFreeFormConfigTarget) {
        this.freeFormConfigTarget.style.display = 'block'
        this.enableInputs(this.freeFormConfigTarget)
        this.generateFreeFormLayout()
      }
    }
  }

  // Disable form inputs in a container
  disableInputs(container) {
    container.querySelectorAll('input, select, textarea').forEach(input => {
      input.disabled = true
    })
  }

  // Enable form inputs in a container
  enableInputs(container) {
    container.querySelectorAll('input, select, textarea').forEach(input => {
      input.disabled = false
    })
  }

  generateGrid() {
    const rows = parseInt(this.rowsTarget.value) || 0
    const columns = parseInt(this.columnsTarget.value) || 0
    console.log("[SeatLayoutBuilder] generateGrid called - rows:", rows, "columns:", columns)

    if (rows === 0 || columns === 0) {
      console.log("[SeatLayoutBuilder] Rows or columns is 0, showing placeholder")
      this.gridDisplayTarget.innerHTML = `
        <p class="text-sm text-gray-500 text-center">Enter rows and columns to preview the layout</p>
      `
      this.updateSeatCount(0)
      if (this.hasBulkActionsTarget) this.bulkActionsTarget.style.display = 'none'
      return
    }

    if (rows > 20 || columns > 20) {
      this.gridDisplayTarget.innerHTML = `
        <p class="text-sm text-red-500 text-center">Maximum 20 rows and 20 columns allowed</p>
      `
      if (this.hasBulkActionsTarget) this.bulkActionsTarget.style.display = 'none'
      return
    }

    // Show bulk actions when grid is displayed
    if (this.hasBulkActionsTarget) this.bulkActionsTarget.style.display = 'flex'

    let gridHTML = '<div class="space-y-3">'

    // Add a "FRONT" indicator with better styling
    gridHTML += `
      <div class="text-center mb-6 pb-3 border-b-4 border-gray-400 rounded-sm">
        <span class="text-sm font-bold text-gray-700 uppercase tracking-widest bg-gray-200 px-4 py-1 rounded-full">↑ Front ↑</span>
      </div>
    `

    let totalSeats = 0
    let availableSeats = 0

    for (let row = 0; row < rows; row++) {
      const rowLetter = String.fromCharCode(65 + row) // A, B, C, etc.
      gridHTML += '<div class="flex items-center gap-3">'

      // Row label with better styling
      gridHTML += `<div class="w-8 text-sm font-bold text-gray-600 text-right">${rowLetter}</div>`

      // Seats in this row
      gridHTML += '<div class="flex gap-2">'
      for (let col = 0; col < columns; col++) {
        const seatKey = `${row}-${col}`
        const seatNumber = `${rowLetter}${col + 1}`
        const isBlocked = this.blockedSeats.has(seatKey)

        totalSeats++
        if (!isBlocked) availableSeats++

        const seatClass = isBlocked
          ? 'bg-gray-300 border-gray-500 text-gray-700 cursor-pointer hover:bg-gray-400 hover:scale-105'
          : 'bg-indigo-100 border-indigo-600 text-indigo-900 cursor-pointer hover:bg-indigo-200 hover:scale-105'

        gridHTML += `
          <button
            type="button"
            class="w-12 h-12 ${seatClass} border-2 rounded-lg flex items-center justify-center text-xs font-bold transition-all duration-150 shadow-sm"
            data-seat-key="${seatKey}"
            data-seat-number="${seatNumber}"
            data-action="click->seat-layout-builder#toggleSeat"
            title="${isBlocked ? 'Click to unblock' : 'Click to block'} seat ${seatNumber}"
          >
            ${seatNumber}
          </button>
        `
      }
      gridHTML += '</div></div>'
    }

    gridHTML += '</div>'
    this.gridDisplayTarget.innerHTML = gridHTML

    // Update seat count
    this.updateSeatCount(availableSeats)

    // Update configuration
    this.updateConfiguration()
  }

  toggleSeat(event) {
    event.preventDefault()
    const button = event.currentTarget
    const seatKey = button.dataset.seatKey
    const seatNumber = button.dataset.seatNumber

    if (this.blockedSeats.has(seatKey)) {
      // Unblock the seat
      this.blockedSeats.delete(seatKey)
      button.className = button.className.replace(
        'bg-gray-300 border-gray-500 text-gray-700 cursor-pointer hover:bg-gray-400 hover:scale-105',
        'bg-indigo-100 border-indigo-600 text-indigo-900 cursor-pointer hover:bg-indigo-200 hover:scale-105'
      )
      button.title = `Click to block seat ${seatNumber}`
    } else {
      // Block the seat
      this.blockedSeats.add(seatKey)
      button.className = button.className.replace(
        'bg-indigo-100 border-indigo-600 text-indigo-900 cursor-pointer hover:bg-indigo-200 hover:scale-105',
        'bg-gray-300 border-gray-500 text-gray-700 cursor-pointer hover:bg-gray-400 hover:scale-105'
      )
      button.title = `Click to unblock seat ${seatNumber}`
    }

    // Update seat count and configuration
    const rows = parseInt(this.rowsTarget.value) || 0
    const columns = parseInt(this.columnsTarget.value) || 0
    const totalSeats = rows * columns
    const availableSeats = totalSeats - this.blockedSeats.size

    this.updateSeatCount(availableSeats)
    this.updateConfiguration()
  }

  generateRowLayout() {
    const numRows = parseInt(this.rowLayoutRowsTarget.value) || 0
    console.log("[SeatLayoutBuilder] generateRowLayout called - rows:", numRows)

    if (numRows === 0) {
      this.rowDetailsTarget.innerHTML = `
        <p class="text-sm text-gray-500 text-center">Enter the number of rows to configure</p>
      `
      if (this.hasRowSeatCountTarget) this.rowSeatCountTarget.textContent = '0'
      return
    }

    if (numRows > 30) {
      this.rowDetailsTarget.innerHTML = `
        <p class="text-sm text-red-500 text-center">Maximum 30 rows allowed</p>
      `
      return
    }

    // Load existing configuration if available
    let existingSeatsPerRow = {}
    try {
      const config = JSON.parse(this.configFieldTarget.value || '{}')
      if (config.seatsPerRow) {
        existingSeatsPerRow = config.seatsPerRow
      }
    } catch (e) {
      console.error("Error parsing configuration:", e)
    }

    let html = '<div class="space-y-3">'
    html += '<p class="text-sm font-medium text-gray-700">Seats per row:</p>'

    for (let i = 0; i < numRows; i++) {
      const rowLetter = String.fromCharCode(65 + i) // A, B, C, etc.
      const existingValue = existingSeatsPerRow[rowLetter] || ''

      html += `
        <div class="flex items-center gap-4">
          <label class="w-20 text-sm font-medium text-gray-700">Row ${rowLetter}</label>
          <input
            type="number"
            min="1"
            max="50"
            value="${existingValue}"
            placeholder="10"
            data-row="${rowLetter}"
            data-action="input->seat-layout-builder#updateRowConfiguration"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
          <span class="text-sm text-gray-500">seats</span>
        </div>
      `
    }

    html += '</div>'
    this.rowDetailsTarget.innerHTML = html

    this.updateRowConfiguration()
  }

  updateRowConfiguration() {
    const seatsPerRow = {}
    let totalSeats = 0

    // Collect all seat counts from row inputs
    this.rowDetailsTarget.querySelectorAll('input[data-row]').forEach(input => {
      const row = input.dataset.row
      const seats = parseInt(input.value) || 0
      if (seats > 0) {
        seatsPerRow[row] = seats
        totalSeats += seats
      }
    })

    // Update seat count display
    if (this.hasRowSeatCountTarget) {
      this.rowSeatCountTarget.textContent = totalSeats
    }

    // Check capacity warning
    if (this.hasRowCapacityWarningTarget && this.hasRoomCapacityValue) {
      const roomCapacity = this.roomCapacityValue
      if (totalSeats > roomCapacity) {
        this.rowCapacityWarningTarget.innerHTML = `
          <div class="rounded-md bg-red-50 p-4 border border-red-200">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800">Too many seats configured!</h3>
                <p class="mt-1 text-sm text-red-700">
                  You have <strong>${totalSeats} seats</strong> but room capacity is only <strong>${roomCapacity}</strong>.
                  Please reduce the number of seats in some rows.
                </p>
              </div>
            </div>
          </div>
        `
        this.rowCapacityWarningTarget.style.display = 'block'
      } else {
        this.rowCapacityWarningTarget.style.display = 'none'
      }
    }

    // Build seats array for configuration
    const seats = []
    let seatIndex = 0
    Object.entries(seatsPerRow).forEach(([row, count]) => {
      for (let i = 0; i < count; i++) {
        seats.push({
          number: `${row}${i + 1}`,
          row: row,
          x: i,
          y: row.charCodeAt(0) - 65
        })
        seatIndex++
      }
    })

    // Update configuration
    const config = {
      seatsPerRow: seatsPerRow,
      seats: seats
    }
    this.configFieldTarget.value = JSON.stringify(config)
  }

  generateFreeFormLayout() {
    const numSeats = parseInt(this.freeFormSeatsTarget.value) || 0
    console.log("[SeatLayoutBuilder] generateFreeFormLayout called - seats:", numSeats)

    if (numSeats === 0) {
      this.freeFormDetailsTarget.innerHTML = `
        <p class="text-sm text-gray-500 text-center">Enter the number of seats to configure</p>
      `
      if (this.hasFreeFormSeatCountTarget) this.freeFormSeatCountTarget.textContent = '0'
      return
    }

    if (numSeats > 100) {
      this.freeFormDetailsTarget.innerHTML = `
        <p class="text-sm text-red-500 text-center">Maximum 100 seats allowed</p>
      `
      return
    }

    // Load existing configuration if available
    let existingSeatNumbers = {}
    try {
      const config = JSON.parse(this.configFieldTarget.value || '{}')
      if (config.seatNumbers) {
        existingSeatNumbers = config.seatNumbers
      }
    } catch (e) {
      console.error("Error parsing configuration:", e)
    }

    let html = '<div class="space-y-3">'
    html += '<p class="text-sm font-medium text-gray-700">Seat numbers:</p>'
    html += '<div class="grid grid-cols-2 gap-3">'

    for (let i = 0; i < numSeats; i++) {
      const seatIndex = i + 1
      const existingValue = existingSeatNumbers[seatIndex] || `Seat ${seatIndex}`

      html += `
        <div class="flex items-center gap-2">
          <label class="w-16 text-sm text-gray-600">#${seatIndex}</label>
          <input
            type="text"
            value="${existingValue}"
            placeholder="Seat ${seatIndex}"
            data-seat-index="${seatIndex}"
            data-action="input->seat-layout-builder#updateFreeFormConfiguration"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
      `
    }

    html += '</div></div>'
    this.freeFormDetailsTarget.innerHTML = html

    this.updateFreeFormConfiguration()
  }

  updateFreeFormConfiguration() {
    const seatNumbers = {}
    const seats = []

    // Collect all seat numbers from inputs
    this.freeFormDetailsTarget.querySelectorAll('input[data-seat-index]').forEach(input => {
      const index = parseInt(input.dataset.seatIndex)
      const number = input.value || `Seat ${index}`
      seatNumbers[index] = number
      seats.push({
        number: number,
        x: (index - 1) % 10,  // Simple grid positioning for now
        y: Math.floor((index - 1) / 10)
      })
    })

    const totalSeats = seats.length

    // Update seat count display
    if (this.hasFreeFormSeatCountTarget) {
      this.freeFormSeatCountTarget.textContent = totalSeats
    }

    // Check capacity warning
    if (this.hasFreeFormCapacityWarningTarget && this.hasRoomCapacityValue) {
      const roomCapacity = this.roomCapacityValue
      if (totalSeats > roomCapacity) {
        this.freeFormCapacityWarningTarget.innerHTML = `
          <div class="rounded-md bg-red-50 p-4 border border-red-200">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800">Too many seats configured!</h3>
                <p class="mt-1 text-sm text-red-700">
                  You have <strong>${totalSeats} seats</strong> but room capacity is only <strong>${roomCapacity}</strong>.
                  Please reduce the total number of seats.
                </p>
              </div>
            </div>
          </div>
        `
        this.freeFormCapacityWarningTarget.style.display = 'block'
      } else {
        this.freeFormCapacityWarningTarget.style.display = 'none'
      }
    }

    // Update configuration
    const config = {
      seatNumbers: seatNumbers,
      seats: seats
    }
    this.configFieldTarget.value = JSON.stringify(config)
  }

  updateSeatCount(count) {
    if (this.hasSeatCountTarget) {
      this.seatCountTarget.textContent = count
    }

    // Show capacity warning if available seats exceed room capacity
    if (this.hasCapacityWarningTarget && this.hasRoomCapacityValue) {
      const roomCapacity = this.roomCapacityValue

      if (count > roomCapacity) {
        this.capacityWarningTarget.innerHTML = `
          <div class="rounded-md bg-red-50 p-4 border border-red-200">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800">
                  Too many seats configured!
                </h3>
                <p class="mt-1 text-sm text-red-700">
                  You have <strong>${count} available seats</strong> but room capacity is only <strong>${roomCapacity}</strong>.
                  Please block ${count - roomCapacity} more seat(s) or reduce the grid size.
                </p>
              </div>
            </div>
          </div>
        `
        this.capacityWarningTarget.style.display = 'block'
      } else {
        this.capacityWarningTarget.style.display = 'none'
      }
    }
  }

  updateConfiguration() {
    const layoutType = this.layoutTypeTarget.value
    let config = {}

    if (layoutType === 'grid') {
      config = {
        blocked: Array.from(this.blockedSeats),
        labels: this.customLabels
      }
    } else if (layoutType === 'rows') {
      // Build seats array from row inputs
      const seats = []
      this.rowDetailsTarget.querySelectorAll('input[data-row]').forEach(input => {
        const row = input.dataset.row
        const seatsInRow = parseInt(input.value) || 0
        for (let i = 1; i <= seatsInRow; i++) {
          seats.push({
            number: `${row}${i}`,
            row: row,
            x: i - 1,
            y: row.charCodeAt(0) - 65
          })
        }
      })
      config = { seats }
    } else if (layoutType === 'free_form') {
      // Build seats array from free-form inputs
      const seats = []
      this.freeFormDetailsTarget.querySelectorAll('input[data-seat-number]').forEach((input, idx) => {
        const seatNumber = input.value.trim()
        if (seatNumber) {
          seats.push({
            number: seatNumber,
            x: idx % 10,
            y: Math.floor(idx / 10)
          })
        }
      })
      config = { seats }
    }

    this.configFieldTarget.value = JSON.stringify(config)
    console.log("[SeatLayoutBuilder] Updated configuration:", config)
  }

  // Bulk operations for grid layout
  clearAllBlocked(event) {
    event.preventDefault()
    console.log("[SeatLayoutBuilder] Clearing all blocked seats")
    this.blockedSeats.clear()
    this.generateGrid()
  }

  blockAll(event) {
    event.preventDefault()
    console.log("[SeatLayoutBuilder] Blocking all seats")
    const rows = parseInt(this.rowsTarget.value) || 0
    const columns = parseInt(this.columnsTarget.value) || 0

    this.blockedSeats.clear()
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        this.blockedSeats.add(`${row}-${col}`)
      }
    }
    this.generateGrid()
  }

  // Handle form submission - ensure configuration is updated
  handleSubmit(event) {
    console.log("[SeatLayoutBuilder] Form submitting, updating configuration...")
    this.updateConfiguration()
    console.log("[SeatLayoutBuilder] Configuration updated:", this.configFieldTarget.value)
  }
}
